/**
 * Home — four screens wearing one name.
 *
 * REBUILT 23 AUGUST to Tim's phase brief. What this replaced was a fixed page: two columns, the
 * same seven cards all year, and a couple of `phase === …` conditionals bolted on the end. That is
 * a reasonable Home for one fortnight of the year and a slightly wrong one for the other fifty
 * weeks — in January it opened on a keeper slate nobody had thought about, and on draft night it
 * opened on the rule-proposal deadline.
 *
 * The order of the page is now `homePlan.homeSections`, which is a table keyed by phase, and the
 * argument for putting it there rather than here is that the brief is a CONTENT spec — "bring the
 * deadlines to the forefront", "remove year in review", "draft front and center" — and a table can
 * be read against those sentences one line at a time. `tests/homePlan.test.ts` does exactly that.
 * This file is the drawing of it.
 *
 * THREE RANKS, THREE PLACES ON THE PAGE. `lead` is a full-width band above everything, `primary`
 * flows through the two-column body, `secondary` is a quieter row at the foot. A section that is
 * not returned is NOT RENDERED — greying out a card nobody needs in January is still a card in
 * January.
 *
 * The design rule from the old screen survives because it is still right: nothing here is a place
 * to DO work. Every card states a fact and links to the screen where the work happens. The one
 * exception is the meeting link, which is a door rather than a form.
 */

import { useEffect, useMemo, useState } from 'react';
import farmLog from '../data/farm-log.json';
import keeperRows from '../data/keepers-2026.json';
import last from '../data/season-2025.json';
import { projectedOrder } from '../domain/season2025.js';
import { LEAGUE_2026 } from '../domain/config.js';
import { openingBalance } from '../domain/faab.js';
import { assessLuxuryTax } from '../domain/luxuryTax.js';
import { committeeOver, liveLeft, type Urgency } from '../domain/deadlines.js';
import { deadlineRows, type DeadlineRow } from '../domain/deadlineTable.js';

import { chasing, commishTrack, homeSections, homeTasks, type HomeTask } from '../domain/homePlan.js';
import { pickDue, pickWatch } from '../domain/pickDue.js';
import { swapOutstanding } from '../domain/thirdPlaceSwap.js';
import { canSeeTrade, stillListed } from '../domain/tradeWindow.js';
import { GMS } from '../store/session.js';
import { Citations, Linkify } from './RuleText.js';
import { useTick } from './useTick.js';
import { rankingCensus } from '../domain/rankingCensus.js';
import { onDocket, undecided } from '../domain/proposals.js';
import { holdingsFor, ownership2026 } from '../domain/picks2026.js';
import { liveBoard } from '../domain/liveBoard.js';
import { adpByName, adpKey } from '../domain/draftTools.js';
import { useOwnership } from './useOwnership.js';
import { gmOfTeam } from '../domain/identity.js';
import { WIRE_FEED_LENGTH, WIRE_LENGTH, wire } from '../domain/wire.js';
import {
  CommishCard, DoorCard, DueRail, DueRing, NextAction, OrderCard, TaskTiles, ViewerNote, Wire,
} from './HomeParts.js';
import {
  CommitteeCard, FaabLead, FarmCard, FarmTargetsCard, NextTen, PickRundown, PicksStrip,
  RosterByPosition, SlateCard, SlotPips, TargetAdd, TradesCard, WageringCard, YearInReview,
  type PickCell, type SlateStatus,
} from './HomeCards.js';
import prospectData from '../data/prospects-2027.json';
import type { Chrome } from './chrome.js';
import { PickClock } from './PickClock.js';
import { pickSeconds } from '../domain/clocks.js';
import { norm } from '../domain/prospectBank.js';
import type { FarmNomination, FarmTargetList } from '../store/types.js';
import {
  atLeast, type DraftPick, type DraftState, type FaabEntry, type KeeperDeclaration,
  type LeagueSettings, type LeagueStore, type LiveTradeDoc, type Role, type RuleProposal,
} from '../store/types.js';

const SEASON = 2026;
const TEAMS = LEAGUE_2026.teams;

const LAST = last as {
  season: number;
  standings: Array<{ gm: string; rank: number; team: string; w: number; l: number; pf: number }>;
  champion: string; runnerUp: string; thirdPlace: string; fourthPlace: string;
  toiletBowlWinner: string;
};
/*
 * THE BASIS, NOT THE ORDER OF FINISH. "Worst finisher first" means the table the rules rank last
 * season by, and since Pass 06 §1 that is the regular season below the playoff places — so the
 * chalk projection and the reveal's weights both read `LOTTERY_BASIS`. `season2025.ts` is the one
 * place the two orderings are combined.
 */

interface FarmRow {
  player: string | null; gm: string; pos?: string; school?: string;
  status?: string; farmYear: number;
}

/*
 * WHAT A KEEPER WAS BOUGHT FOR IS NOT WHAT KEEPING HIM COSTS, and this card used to show the
 * first while claiming to be about the second. The per-row FAAB figure is gone; the slate is
 * totalled instead. `KeeperRowLite` survives because `POS_OF` still needs it.
 */
interface KeeperRowLite { player: string; pos?: string }
const POS_OF: Record<string, string> = Object.fromEntries(
  (keeperRows as unknown as KeeperRowLite[]).map((r) => [r.player, r.pos ?? '']),
);

/**
 * College players still on the farm, by GM, as the filed log has them.
 *
 * THE LOG IS NOT THE WHOLE ANSWER ON DRAFT NIGHT (Tim, 9 September: "my farm should display on
 * home"). `farm-log.json` is built with the app, so a man won an hour ago on the auction screen is
 * not in it and cannot be — Home showed Tim three empty slots on the evening he had just bought
 * somebody. Tonight's wins are merged in below, the same way the auction's own roster panel does
 * it; see `farmOf`.
 */
const FARM_BY_GM = (() => {
  const out: Record<string, FarmRow[]> = {};
  for (const r of farmLog as unknown as FarmRow[]) {
    if (!r.gm || r.status !== 'college' || !r.player) continue;
    (out[r.gm.toUpperCase()] ??= []).push(r);
  }
  return out;
})();

interface Props {
  gm: string;
  role: Role;
  store: LeagueStore;
  onGo: (section: string, page: string) => void;
  chrome: Chrome;
}

export function Home({ gm, role, store, onGo, chrome }: Props) {
  const [settings, setSettings] = useState<LeagueSettings | null>(null);
  const [draft, setDraft] = useState<DraftState | null>(null);
  const [decls, setDecls] = useState<KeeperDeclaration[]>([]);
  const [mine, setMine] = useState<KeeperDeclaration | null>(null);
  const [prefs, setPrefs] = useState<
    Array<{ gm: string; ranked: number[]; submittedAt?: string | null }>>([]);
  const [faab, setFaab] = useState<FaabEntry[]>([]);
  const [proposals, setProposals] = useState<RuleProposal[]>([]);
  const [picks, setPicks] = useState<DraftPick[]>([]);
  const [live, setLive] = useState<LiveTradeDoc[]>([]);
  const [targets, setTargets] = useState<FarmTargetList | null>(null);
  const [noms, setNoms] = useState<FarmNomination[]>([]);
  const [tagging, setTagging] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  /**
   * ONE FAILING READ MUST NOT BLANK THE PAGE.
   *
   * `allSettled`, not `all`: any one rejection used to leave every piece of state at its initial
   * value, and Home rendered a confident, completely wrong screen. "No date set" beside a deadline
   * that IS set, the opening balance for everybody, "not started", 0 of 3 — with nothing saying
   * anything had gone wrong, because the empty state and the failed state look identical.
   */
  useEffect(() => {
    let alive = true;
    const load = async () => {
      const results = await Promise.allSettled([
        store.settings(SEASON), store.draftState(SEASON), store.declarations(SEASON),
        store.preferences(SEASON), store.faab(SEASON), store.proposals(SEASON),
        store.declarationFor(SEASON, gm), store.picks(SEASON), store.liveTrades(SEASON),
        store.farmTargetsFor(SEASON, gm), store.farmNominations(SEASON),
      ]);
      if (!alive) return;
      const [s, d, dec, p, f, pr, own, pk, lt, ft, fn] = results;
      if (s.status === 'fulfilled') setSettings(s.value);
      if (d.status === 'fulfilled') setDraft(d.value);
      if (dec.status === 'fulfilled') setDecls(dec.value);
      if (p.status === 'fulfilled') setPrefs(p.value);
      if (f.status === 'fulfilled') setFaab(f.value);
      if (pr.status === 'fulfilled') setProposals(pr.value);
      if (own.status === 'fulfilled') setMine(own.value);
      if (pk.status === 'fulfilled') setPicks(pk.value);
      if (lt.status === 'fulfilled') setLive(lt.value);
      if (ft.status === 'fulfilled') setTargets(ft.value);
      if (fn.status === 'fulfilled') setNoms(fn.value);

      const named = ['settings', 'the draft', 'declarations', 'rankings', 'FAAB', 'proposals',
        'your slate', 'the picks', 'live trades', 'your farm targets', 'the farm auction'];
      const failed = results
        .map((r, i) => (r.status === 'rejected' ? named[i]! : null))
        .filter((x): x is string => x !== null);
      setLoadError(failed.length === 0 ? null : failed.join(', '));
      for (const [i, r] of results.entries()) {
        if (r.status === 'rejected') console.error(`Home could not read ${named[i]}`, r.reason);
      }
    };
    void load();
    const off = store.subscribe(SEASON, () => void load());
    return () => { alive = false; off(); };
  }, [store, gm]);

  const me = gm.toUpperCase();
  const commish = atLeast(role, 'commissioner');
  const viewer = role === 'viewer';
  const { phase, focus } = chrome;

  /* ------------------------------------------------------------------------ the ticking now */

  const still = Date.now();
  const rows = useMemo(() => deadlineRows(settings, {
    declared: { of: TEAMS, done: decls.filter((d) => d.status === 'submitted').length },
    preferences: { of: TEAMS, done: prefs.length },
    onDocket: onDocket(proposals),
  }, still).sort(byUrgency), [settings, decls, prefs, proposals, still]);

  const anyToday = rows.some((r) => r.urgency === 'day');
  const now = useTick(anyToday, 1000) || still;

  /* ------------------------------------------------------------------- what this manager owes */

  const myDecl = mine ?? decls.find((d) => d.gm.toUpperCase() === me);
  const myPref = prefs.find((p) => p.gm.toUpperCase() === me);
  const published = Boolean(draft?.published && draft.order?.length === TEAMS);
  const submitted = decls.filter((d) => d.status === 'submitted').length;

  const tasks = useMemo(() => homeTasks({
    phase,
    focus,
    now,
    proposalDeadline: settings?.proposalDeadline,
    keeperDeadline: settings?.keeperDeadline,
    preferenceDeadline: settings?.preferenceDeadline,
    committeeDate: settings?.committeeDate,
    draftDate: settings?.draftDate,
    declarationsLocked: settings?.declarationsLocked,
    preferencesLocked: settings?.preferencesLocked,
    wroteProposal: proposals.some((p) => p.gm.toUpperCase() === me),
    declared: myDecl?.status === 'submitted',
    ranked: (myPref?.ranked.length ?? 0) > 0,
    maxKeepers: LEAGUE_2026.maxKeepers,
    freeKeepers: LEAGUE_2026.freeKeepers,
    swapOutstanding: (() => {
      const item = swapOutstanding({
        thirdPlace: draft?.thirdPlace ?? null,
        fourthPlace: draft?.fourthPlace ?? null,
        selectionOrder: draft?.selectionOrder ?? [],
        drawnAt: draft?.lotteryDrawnAt ?? null,
        deadline: settings?.preferenceDeadline ?? null,
        declaration: draft?.thirdFourthDeclaration ?? null,
        now,
      });
      return item && item.gm.toUpperCase() === me ? { title: item.title } : null;
    })(),
  }), [phase, focus, now, settings, proposals, me, myDecl, myPref, draft]);

  /* --------------------------------------------------------------------------- draft night */

  const due = useMemo(() => pickDue({
    status: draft?.status,
    onClock: chrome.strip.onClock
      ? { ...chrome.strip.onClock, overall: overallOfCode(chrome.strip.onClock.code) }
      : null,
    mine: chrome.strip.mine,
    since: chrome.strip.since,
    clocks: settings?.clocks,
    now,
  }), [draft?.status, chrome.strip, settings?.clocks, now]);
  const watch = pickWatch(due);

  /**
   * WHOSE TEAM HE IS ON, NOT WHO TYPED HIM IN (Tim, 8 September: "somehow my home screen is
   * showing 32 players are on my team -- is it counting all of the picks I placed as the admin,
   * not just the ones actually on my fantasy team?"). It was.
   *
   * `madeBy` is attribution: the man at the keyboard, which on draft night is a commissioner
   * entering picks for a room of ten. `gm` is the owner — "who owns the pick now, after trades",
   * which is the only one of the two that says whose roster a player lands on. Reading `madeBy`
   * first put every pick Tim entered for anybody onto Tim's roster: thirty-two players on a
   * sixteen-round team, and the count matched the whole board's `32 of 160` exactly.
   *
   * A commissioner is still recorded as himself everywhere it matters — the audit log keeps
   * `madeBy` with `onBehalfOf` beside it. It just is not a fact about a roster.
   */
  const myPicks = useMemo(
    () => picks.filter((p) => p.player && p.gm?.toUpperCase() === me),
    [picks, me],
  );

  /* --------------------------------------------------------------------------------- money */

  const balances = useMemo(() => {
    const out: Record<string, number> = Object.fromEntries(GMS.map((g) => [g, openingBalance(g)]));
    for (const e of faab) {
      const g = e.gm.toUpperCase();
      out[g] = (out[g] ?? openingBalance(g)) + e.amount;
    }
    return out;
  }, [faab]);
  const myFaab = balances[me] ?? 0;
  const faabRank = useMemo(() => {
    const better = GMS.filter((g) => (balances[g] ?? 0) > myFaab).length;
    const tied = GMS.filter((g) => (balances[g] ?? 0) === myFaab).length;
    return { place: better + 1, tied: tied > 1 };
  }, [balances, myFaab]);
  const myLedger = useMemo(() => [...faab]
    .filter((e) => e.gm.toUpperCase() === me)
    .sort((a, b) => b.date.localeCompare(a.date))
    .slice(0, 3), [faab, me]);

  /* --------------------------------------------------------------------------------- slate */

  /**
   * THE ROUND THE LEAGUE RECORDED, not a round Home worked out for itself.
   *
   * `chargedPick` is written by the declare screen when it resolves a slate against the pick
   * ledger, and re-deriving it here is how Home and that screen come to disagree about which of a
   * GM's two twelfths a keeper stands on. A row the resolver could not place carries no round, and
   * that is drawn as a problem rather than as a blank.
   */
  const slateRows = useMemo(() => (myDecl?.keepers ?? []).map((k) => ({
    player: k.player,
    pos: POS_OF[k.player] ?? '',
    round: k.chargedPick?.round ?? k.elevatedTo ?? null,
    tagged: Boolean(k.franchiseTagged),
  })), [myDecl]);

  const slateCost = useMemo(() => {
    const declared = (myDecl?.keepers ?? []).map((k) => ({
      playerId: k.player,
      franchiseTagged: k.franchiseTagged,
      ...(k.farmForfeited ? { farmForfeited: true } : {}),
    }));
    const tax = assessLuxuryTax({ teamId: me, declared }, LEAGUE_2026);
    return {
      slots: tax.slotsUsed,
      tax: tax.taxFaab,
      charged: faab.some((e) => e.gm.toUpperCase() === me && e.kind === 'luxury-tax'),
    };
  }, [myDecl, me, faab]);

  const slateStatus: SlateStatus = myDecl?.status === 'submitted' ? 'submitted'
    : myDecl ? 'draft' : 'none';

  /* ----------------------------------------------------------- the picks a slate leaves behind */

  const pickCells = useMemo<PickCell[]>(() => {
    const held = new Set(
      holdingsFor(me, ownership2026((t) => gmOfTeam(t)), LEAGUE_2026.draftRounds)
        .map((h) => h.round),
    );
    const spent = new Set(slateRows.map((r) => r.round).filter((r): r is number => r !== null));
    return Array.from({ length: LEAGUE_2026.draftRounds }, (_, i) => {
      const round = i + 1;
      if (!held.has(round)) return 'gone';
      return spent.has(round) ? 'keeper' : 'open';
    });
  }, [me, slateRows]);

  /* --------------------------------------------------------------------------------- the rest */

  const projected = useMemo(() => {
    const { order } = projectedOrder(draft, TEAMS);
    const i = order.findIndex((g) => g.toUpperCase() === me);
    const top = order.slice(0, 4).map((g, n) => ({ g, i: n }));
    if (i >= 4) top.splice(3, 1, { g: order[i]!, i });
    return { published, shown: top, more: Math.max(0, order.length - 4) };
  }, [draft, me, published]);

  /**
   * What he holds right now: the filed log plus anything he has won tonight.
   *
   * A win is his the moment the hammer falls -- that is what the cap counts against and what he is
   * deciding his next bid around. Matched on name and season so a season already folded into the
   * log is not shown twice.
   */
  const farm = useMemo(() => {
    const filed = FARM_BY_GM[me] ?? [];
    const seen = new Set(filed.map((r) => norm(r.player ?? "")));
    const tonight = noms
      .filter((n) => n.winner?.toUpperCase() === me && n.player && !seen.has(norm(n.player)))
      .map((n) => ({
        farmYear: n.season,
        gm: me,
        player: n.player!,
        ...(n.position ? { pos: n.position } : {}),
        ...(n.school ? { school: n.school } : {}),
        bid: n.bid ?? null,
        outcome: 'won',
        status: 'college',
        active: true,
      } as FarmRow));
    return [...filed, ...tonight];
  }, [me, noms]);

  /*
   * EVERY TRADE IN FLIGHT, NOT ONLY YOUR OWN (Tim, 8 September: "show pending trades not just
   * that the active GM is involved in on the home page").
   *
   * The card counted the deals a man was IN, so on a night when three trades were live and none
   * of them were his it read "none open" — which is true about him and false about the league, and
   * the league is the thing he is about to draft against. `canSeeTrade` still decides what he may
   * know: an unaffirmed deal is two men's private business until it is affirmed
   * (2022-trade-declarations §1), so those stay invisible to the other eight and visible to a
   * commissioner.
   *
   * His own are counted separately rather than lost in the total — "two open, one of them yours"
   * answers both questions in one line.
   */
  const openTrades = useMemo(() => {
    const ctx = { draftDate: settings?.draftDate, inSeason: phase === 'inseason', now };
    return live.filter((t) => stillListed(t, ctx) && canSeeTrade(t, me, commish));
  }, [live, me, commish, settings?.draftDate, phase, now]);
  const myTrades = useMemo(
    () => openTrades.filter((t) => t.sides.some((sd) => sd.gm.toUpperCase() === me)),
    [openTrades, me],
  );

  /* ------------------------------------------------------------------------ the night itself */

  /**
   * THE BOARD THE ROOM SEES, not the raw picks collection (Tim, 8 September: "his pick rundown
   * should include his keepers/farm/franchised players and so should his roster").
   *
   * A keeper is not a saved pick. He is derived — from the declaration, the Grid's cost round,
   * the rounds his GM holds after trades, and the escalation that pushes a collision earlier. The
   * draft board did that derivation privately, so Home read `picks`, found nothing, and drew a
   * manager sixteen empty rounds and an empty roster while the board beside it had four of his
   * keepers, a farm graduate and a franchise tag on it.
   */
  const { ownership: heldPicks } = useOwnership(store);
  const roomBoard = useMemo(() => liveBoard({
    order: draft?.order ?? [],
    rounds: LEAGUE_2026.draftRounds,
    ownership: heldPicks,
    declarations: decls,
    saved: new Map(picks.filter((p) => p.player).map((p) => [p.overall, {
      player: p.player, position: p.position, madeAt: p.madeAt, madeBy: p.madeBy,
    }])),
  }).board, [draft?.order, heldPicks, decls, picks]);

  /**
   * WHERE THE CONSENSUS TAKES A MAN, for ordering a roster (Tim, 8 September: "as roster is
   * filled in, the players are to be ordered by their ADP"). A roster in the order the picks
   * happened to be made is a list of accidents; in ADP order it is a read on the team. Anybody
   * the consensus has never heard of sorts last rather than first, because an unranked rookie is
   * not the best player on the roster.
   */
  const adpOf = useMemo(() => {
    const m = adpByName();
    return (player: string) => m.get(adpKey(player)) ?? Infinity;
  }, []);

  /**
   * YOUR OWN PICKS, IN ORDER, WITH THE ROUNDS YOU SOLD SIMPLY ABSENT.
   *
   * A traded round belongs to somebody else; drawing it as an empty row of yours would tell you
   * that you are up in a round you sold in November. The count in the header is therefore of the
   * picks you hold rather than of the sixteen rounds the draft has.
   */
  const rundown = useMemo(() => {
    const holdings = holdingsFor(me, ownership2026((t) => gmOfTeam(t)), LEAGUE_2026.draftRounds);
    const byRound = new Map<number, DraftPick>();
    for (const p of picks) {
      /* The owner, for the same reason as `myPicks` above: a rundown of the rounds you hold
         was showing whichever pick you happened to type first in each of them. */
      if (p.gm.toUpperCase() !== me) continue;
      if (!byRound.has(p.round)) byRound.set(p.round, p);
    }
    const clockRound = due.mine?.code ? Number(due.mine.code.split('.')[0]) : null;
    const tagged = new Set(
      (myDecl?.keepers ?? []).filter((k) => k.franchiseTagged).map((k) => k.player),
    );
    /*
     * A FUTURE PICK'S CODE IS DERIVED, OR IT IS NOT PRINTED.
     *
     * The slot comes from where the seat sits in the published order and which way the snake is
     * running in that round. With no published order there is no slot, and the row reads `Rd 9`
     * rather than inventing `9.05` — the same convention the board and the calculator already use,
     * and the reason is that a made-up pick number is indistinguishable from a real one.
     */
    const order = draft?.order ?? [];
    const seat = order.findIndex((g) => g.toUpperCase() === me) + 1;
    const codeFor = (round: number) => {
      if (order.length !== TEAMS || seat === 0) return `Rd ${round}`;
      const slot = round % 2 === 1 ? seat : TEAMS - seat + 1;
      return `${round}.${String(slot).padStart(2, '0')}`;
    };
    /*
     * A CELL ON THE ROOM'S BOARD BEATS A SAVED PICK, because a keeper only exists there. The
     * saved row still wins where there is one — it is the pick actually made.
     */
    const mineOnBoard = new Map(
      roomBoard.filter((p) => p.owner.toUpperCase() === me).map((p) => [p.round, p]),
    );
    return holdings.map((h) => {
      const p = byRound.get(h.round);
      const cell = mineOnBoard.get(h.round);
      const player = p?.player ?? cell?.player;
      const code = p ? `${p.round}.${String(p.pick).padStart(2, '0')}`
        : cell ? `${cell.round}.${String(cell.slot).padStart(2, '0')}` : codeFor(h.round);
      const kept = Boolean(!p?.player && cell?.keeper);
      return {
        code,
        round: h.round,
        player,
        position: p?.position ?? cell?.pos,
        keeper: (p?.isKeeper || kept) && !tagged.has(player ?? ''),
        franchise: Boolean(player && tagged.has(player)),
        farm: Boolean(kept && cell?.farm),
        onClock: !player && clockRound === h.round && due.state === 'onclock',
      };
    });
  }, [me, picks, due, myDecl, draft?.order, roomBoard]);

  /**
   * What you have drafted, grouped. No kicker: this league does not use one (2019-III-1 §3), and
   * no slot counts, because a roster requirement is a Yahoo setting this app has never read.
   */
  const byPosition = useMemo(() => {
    const order = ['QB', 'RB', 'WR', 'TE', 'DST'];
    const tagged = new Set(
      (myDecl?.keepers ?? []).filter((k) => k.franchiseTagged).map((k) => k.player),
    );
    /* Keepers, farm graduates and tagged men are on the ROOM's board, never in `picks`. */
    const made = new Set(myPicks.map((p) => p.player));
    const held = [
      ...myPicks.map((p) => ({
        player: p.player!, pos: (p.position ?? POS_OF[p.player ?? ''] ?? '').toUpperCase(),
        keeper: Boolean(p.isKeeper), farm: false,
      })),
      ...roomBoard
        .filter((p) => p.owner.toUpperCase() === me && p.player && p.keeper && !made.has(p.player))
        .map((p) => ({
          player: p.player!, pos: (p.pos ?? POS_OF[p.player!] ?? '').toUpperCase(),
          keeper: true, farm: Boolean(p.farm),
        })),
    ];
    return order.map((pos) => ({
      pos,
      players: held
        .filter((p) => p.pos === pos)
        .sort((a, b) => adpOf(a.player) - adpOf(b.player) || a.player.localeCompare(b.player))
        .map((p) => ({
          player: p.player,
          keeper: p.keeper && !tagged.has(p.player),
          franchise: tagged.has(p.player),
          farm: p.farm,
        })),
    }));
  }, [myPicks, myDecl, roomBoard, me, adpOf]);

  /** The next ten picks off the published order, names only. */
  const nextTen = useMemo(() => {
    const order = draft?.order ?? [];
    if (order.length !== TEAMS || !chrome.strip.onClock) return [];
    const from = overallOfCode(chrome.strip.onClock.code);
    const out: Array<{ code: string; gm: string; clock?: string | null }> = [];
    for (let overall = from; overall < from + 10; overall += 1) {
      const round = Math.ceil(overall / TEAMS);
      if (round > LEAGUE_2026.draftRounds) break;
      const slot = overall - (round - 1) * TEAMS;
      /* Snake: even rounds run backwards, which is what the board itself does. */
      const seat = round % 2 === 1 ? slot : TEAMS - slot + 1;
      const who = order[seat - 1];
      if (!who) continue;
      out.push({
        code: `${round}.${String(slot).padStart(2, '0')}`,
        gm: who,
        clock: overall === from ? (due.clock?.text ?? due.elapsed ?? null) : null,
      });
    }
    return out;
  }, [draft?.order, chrome.strip.onClock, due]);

  /* ------------------------------------------------------------------------- the watch list */

  const nominatedBy = useMemo(() => {
    const out = new Map<string, string>();
    for (const n of noms) {
      if (n.player) out.set(n.player.toLowerCase(), n.winner ?? n.nominator);
    }
    return out;
  }, [noms]);

  const targetRows = useMemo(() => (targets?.targets ?? []).map((t) => ({
    player: t.player,
    position: t.position,
    school: t.school,
    note: t.note,
    nominatedBy: nominatedBy.get(t.player.toLowerCase()) ?? null,
  })), [targets, nominatedBy]);

  const PROSPECTS = useMemo(() => (prospectData as {
    prospects: Array<{ n: string; p?: string; s?: string }>;
  }).prospects, []);

  const saveTargets = (next: FarmTargetList['targets']) => {
    const doc: FarmTargetList = {
      season: SEASON, gm: me, targets: next, updatedAt: new Date().toISOString(),
    };
    setTargets(doc);
    void store.saveFarmTargets(doc);
  };

  const census = useMemo(() => rankingCensus(prefs, TEAMS), [prefs]);
  const drawn = Boolean(draft?.lotteryDrawnAt) || (draft?.selectionOrder?.length ?? 0) === TEAMS;

  const track = useMemo(() => commishTrack({
    phase,
    focus,
    teams: TEAMS,
    proposals: onDocket(proposals),
    proposalsUndecided: undecided(proposals),
    declared: submitted,
    ranked: census.willResolve,
    rankedNote: census.note,
    lotteryRun: drawn,
    orderPublished: published,
    onClock: watch && watch.owner ? { line: watch.line, chase: watch.chase } : null,
    picksMade: picks.filter((p) => p.player).length,
    picksTotal: phase === 'draftday' ? TEAMS * LEAGUE_2026.draftRounds : 0,
  }), [phase, focus, proposals, submitted, census, drawn, published, watch, picks]);

  /* ----------------------------------------------------------------------------- the wire */

  /* The whole feed once; the ticker is its first fifteen, so the two never disagree about order. */
  const feed = useMemo(() => wire({
    declarations: decls.map((d) => {
      const t = assessLuxuryTax({
        teamId: d.gm,
        declared: d.keepers.map((k) => ({
          playerId: k.player,
          franchiseTagged: k.franchiseTagged,
          ...(k.farmForfeited ? { farmForfeited: true } : {}),
        })),
      }, LEAGUE_2026);
      return {
        gm: d.gm, status: d.status, submittedAt: d.submittedAt, updatedAt: d.updatedAt,
        /* Men, not slots: the tag's three are priced in the tax, not counted as three keepers. */
        kept: d.keepers.length, tax: t.taxFaab,
      };
    }),
    faab,
    trades: live,
    picks,
    proposals,
  }, WIRE_FEED_LENGTH), [decls, faab, live, picks, proposals]);
  const lines = useMemo(() => feed.slice(0, WIRE_LENGTH), [feed]);

  /* ------------------------------------------------------------------------- the composition */

  const committeeMet = committeeOver(settings?.committeeDate, now);
  const sections = useMemo(
    () => homeSections({ phase, focus, commish, committeeMet }),
    [phase, focus, commish, committeeMet],
  );

  /**
   * WHICH LEAD, AND IT IS THREE QUESTIONS IN ORDER.
   *
   * Inside a day, one thing with a clock on it. Otherwise, more than one thing outstanding gets
   * the ring and the tiles — a figure in days and the two tasks under it, still sorted so the
   * first is the one that shuts first. One thing left gets the card again, without the clock.
   *
   * A phase with no tasks at all (draft day, in season) never reaches any of this; its own lead is
   * in the section table.
   */
  /*
   * OUTSTANDING MEANS OPEN AND NOT DONE, AND IT USED TO MEAN ONLY THE SECOND HALF.
   *
   * Tim, 3 September, two days out from the draft: "the rule change deadline has passed and we're
   * just a couple days before the draft. There's no reason for a big card that tells me that real
   * submissions are closed." He was looking at `Propose a rule change` as the lead card, above the
   * fold, with a greyed-out button and a sentence explaining that a commissioner could reopen it.
   *
   * The cause is the definition on this line. A task he never did and can no longer do is not
   * done, so it counted as outstanding, sorted first because its deadline was the earliest, and
   * took the lead — and once every task in the phase is shut, the page leads with the least useful
   * of them. A shut door is not a thing on your list; it is a fact, and the deadline rail below
   * already records it as one, with the league's count beside it.
   *
   * Consequence to keep in mind: with nothing outstanding the phase's own lead takes the page
   * back, which in pre-draft is `due`. That is the right answer two days out.
   */
  const openTasks = tasks.filter((t) => !t.done && t.open);
  const first = openTasks[0];
  const ticking = Boolean(first && first.urgency === 'day');
  /*
   * THE RING NEEDS A DATE TO BE A RING. It draws a share of a window and a figure in days, and
   * neither exists for a task whose deadline has not been announced. That state falls back to the
   * card, which already knows how to say "no date set" without inventing a number.
   */
  const ringable = Boolean(first?.iso) && openTasks.length > 1 && !ticking;

  const card = (id: string) => {
    switch (id) {
      case 'due':
        return (
          <DueRail
            key="due"
            rows={rows}
            now={now}
            title={phase === 'offseason' ? 'Pre-season checklist' : 'What is due'}
            commish={commish}
            onGo={onGo}
          />
        );

      case 'yearInReview': {
        const top = LAST.standings.slice(0, 3)
          .map((r) => ({ gm: r.gm, rank: r.rank, w: r.w, l: r.l, pf: r.pf }));
        const row = LAST.standings.find((r) => r.gm.toUpperCase() === me);
        return (
          <YearInReview
            key="yir"
            season={LAST.season}
            top={top}
            mine={row ? { rank: row.rank, w: row.w, l: row.l, pf: row.pf } : null}
            onGo={() => onGo('history', 'hub')}
          />
        );
      }

      case 'history':
        return (
          <DoorCard
            key="history"
            title="League history"
            fact="23 seasons · 1,770 games · every draft since 2003"
            quiet
            onGo={() => onGo('history', 'hub')}
          />
        );

      case 'proposals': {
        const mineCount = proposals.filter((p) => p.gm.toUpperCase() === me).length;
        return (
          <section className="card proposals" key="proposals">
            <div className="cardhead">
              <h3>Rule proposals</h3>
              {!settings?.proposalDeadline && <span className="pill dashed">no date</span>}
              <button type="button" className="linkbtn" onClick={() => onGo('league', 'proposals')}>
                {mineCount > 0 ? 'Write another' : 'Write one'}
              </button>
            </div>
            <p className="fact">
              Drafts stay private until you submit them.{' '}
              {settings?.proposalDeadline
                ? `${onDocket(proposals)} on the docket so far.`
                : 'The commissioners have not announced the proposal deadline.'}
            </p>
          </section>
        );
      }

      case 'committee':
        return (
          <CommitteeCard
            key="committee"
            iso={settings?.committeeDate ?? null}
            link={settings?.committeeLink ?? null}
            left={liveLeft(settings?.committeeDate ?? null, now)?.text ?? null}
            docket={onDocket(proposals)}
            lotteryRun={drawn}
            onGo={onGo}
          />
        );

      case 'keeperSlate':
        return (
          <SlateCard
            key="slate"
            status={slateStatus}
            rows={slateRows}
            slots={slateCost.slots}
            tax={slateCost.tax}
            charged={slateCost.charged}
            balance={myFaab}
            deadline={settings?.keeperDeadline ?? null}
            onGo={() => onGo('keepers', 'slate')}
          />
        );

      case 'prefs': {
        const ranked = myPref?.ranked.length ?? 0;
        return (
          <section className="card prefs" key="prefs">
            <div className="cardhead">
              <h3>My slot ranking</h3>
              <span className="pill outline">
                {ranked === TEAMS ? 'all ten' : ranked > 0 ? `${ranked} of ${TEAMS}` : 'not started'}
              </span>
              <button type="button" className="linkbtn" onClick={() => onGo('draft', 'order')}>
                {ranked > 0 ? 'Change it' : 'Rank slots'}
              </button>
            </div>
            <p className="fact">
              If nothing is submitted, you receive the earliest slot left when your turn comes.
            </p>
            <div className="cardfoot"><Citations ids={['2020-II-1']} /></div>
          </section>
        );
      }

      case 'picks':
        return (
          <PicksStrip
            key="picks"
            cells={pickCells}
            balance={myFaab}
            onGo={() => onGo('league', 'faab')}
          />
        );

      case 'order':
        return (
          <OrderCard
            key="order"
            shown={projected.shown}
            more={projected.more}
            me={me}
            published={projected.published}
            onGo={() => onGo('draft', 'order')}
          />
        );

      case 'draftLive':
        return (
          <DraftLead
            key="draftlive"
            due={due}
            state={draft?.status ?? null}
            made={picks.filter((p) => p.player).length}
            total={TEAMS * LEAGUE_2026.draftRounds}
            since={chrome.strip.since}
            seconds={due.onClock ? pickSeconds(settings?.clocks, due.onClock.round) : null}
            onGo={onGo}
          />
        );

      case 'rundown':
        return (
          <PickRundown
            key="rundown"
            rows={rundown}
            made={rundown.filter((r) => r.player).length}
            total={rundown.length}
          />
        );

      case 'byposition':
        return (
          <RosterByPosition
            key="byposition"
            groups={byPosition}
            made={myPicks.length}
            total={rundown.length}
          />
        );

      case 'nextten':
        return nextTen.length === 0 ? null : (
          <NextTen key="nextten" rows={nextTen} me={me} onGo={() => onGo('draft', 'board')} />
        );

      case 'targets':
        return (
          <div className="targetwrap" key="targets">
            <FarmTargetsCard
              rows={targetRows}
              onAdd={() => setTagging(true)}
              onRemove={(player) => saveTargets(
                (targets?.targets ?? []).filter((t) => t.player !== player),
              )}
              onGo={() => onGo('settings', 'index')}
            />
            {tagging && (
              <TargetAdd
                names={PROSPECTS.map((x) => x.n)}
                onCancel={() => setTagging(false)}
                onAdd={(player, note) => {
                  const hit = PROSPECTS.find((x) => x.n === player);
                  saveTargets([
                    ...(targets?.targets ?? []).filter((t) => t.player !== player),
                    {
                      player,
                      ...(hit?.p ? { position: hit.p } : {}),
                      ...(hit?.s ? { school: hit.s } : {}),
                      ...(note ? { note } : {}),
                      addedAt: new Date().toISOString(),
                    },
                  ]);
                  setTagging(false);
                }}
              />
            )}
          </div>
        );

      case 'farm':
        return (
          <FarmCard
            key="farm"
            rows={farm.map((f) => ({ player: f.player!, pos: f.pos, school: f.school }))}
            max={LEAGUE_2026.maxFarmPlayers}
            showOpen={phase === 'draftday'}
            onGo={() => onGo('keepers', 'farm')}
          />
        );

      case 'trades':
        return (
          <TradesCard
            key="trades"
            open={openTrades.length}
            mine={myTrades.length}
            draftDay={phase === 'draftday'}
            onGo={() => onGo('league', 'trades')}
          />
        );

      case 'faab':
        return (
          <FaabLead
            key="faab"
            balance={myFaab}
            place={faabRank.place}
            tied={faabRank.tied}
            entries={myLedger}
            onGo={() => onGo('league', 'faab')}
          />
        );

      case 'wagering':
        return <WageringCard key="wagering" />;

      case 'commish':
        return (
          <CommishCard key="commish" rows={track} chasing={chasing(track)} onGo={onGo} />
        );

      default:
        return null;
    }
  };

  /*
   * A VIEWER HOLDS NO TEAM, so every card with "my" in its name is meaningless to him and every
   * task is somebody else's. He gets the collective half of whatever phase it is: the dates, the
   * order, the record. Conditional, never disabled in place.
   */
  const forViewer = (id: string) => ['due', 'order', 'history', 'yearInReview', 'committee',
    'proposals', 'draftLive'].includes(id);
  const visible = viewer ? sections.filter((s) => forViewer(s.id)) : sections;

  const lead = visible.filter((s) => s.rank === 'lead');
  const primary = visible.filter((s) => s.rank === 'primary');
  const secondary = visible.filter((s) => s.rank === 'secondary');

  /* The lead a phase names is used only where the phase has no task of its own to lead with. */
  const taskLead = !viewer && (phase === 'predraft' || phase === 'offseason') && openTasks.length > 0;

  return (
    <div className="app home">
      <Linkify>
        {viewer && <ViewerNote />}

        {/*
          THE WIRE STANDS DOWN ON DRAFT NIGHT. The live strip is directly above it carrying the
          pick on the clock, and two moving bars stacked at the top of the page is two things
          shouting on the one evening the app has anything to shout about.
        */}
        {phase !== 'draftday' && (
          <Wire lines={lines} feed={feed} onGo={onGo} />
        )}

        {loadError && (
          <p className="loadfail">
            Could not read {loadError}. What is shown below may be incomplete; reload, and if it
            keeps happening the console has the reason.
          </p>
        )}

        {taskLead && first && (!ringable ? (
          <NextAction
            task={first}
            now={now}
            locked={Boolean(first.key === 'keepers' ? settings?.declarationsLocked
              : first.key === 'prefs' ? settings?.preferencesLocked : false)}
            lockedBy={settings?.updatedBy ?? null}
            onGo={onGo}
          />
        ) : (
          <>
            <DueRing
              days={Math.max(0, Math.ceil((Date.parse(first.iso!) - now) / 86_400_000))}
              count={openTasks.length}
              iso={first.iso!}
            />
            <TaskTiles
              tasks={openTasks.slice(0, 3)}
              pips={{
                keepers: (
                  <SlotPips
                    used={slateCost.slots}
                    max={LEAGUE_2026.maxKeepers}
                    free={LEAGUE_2026.freeKeepers}
                  />
                ),
                prefs: (
                  <span className="pips" role="img"
                    aria-label={`${myPref?.ranked.length ?? 0} of ${TEAMS} slots ranked`}>
                    {Array.from({ length: TEAMS }, (_, i) => (
                      <span key={i} className={`pip${i < (myPref?.ranked.length ?? 0) ? ' on' : ''}`} />
                    ))}
                  </span>
                ),
              }}
              onGo={onGo}
            />
          </>
        ))}

        {!taskLead && lead.map((s) => card(s.id))}

        {commish && !viewer && card('commish')}

        {/*
          THE PHASE'S OWN LEAD IS NOT LOST WHEN A TASK TAKES THE TOP.
          Pre-draft leads with the deadline rail; when a manager has something outstanding, the
          next-action card goes above it and the rail becomes the first card of the body. Dropping
          it would take the five dates off the one screen that exists to carry them.
        */}
        {(taskLead ? [...lead, ...primary] : primary).filter((s) => s.id !== 'commish').length > 0 && (
          <div className="homebody">
            {(taskLead ? [...lead, ...primary] : primary)
              .filter((s) => s.id !== 'commish')
              .map((s) => card(s.id))}
          </div>
        )}

        {!viewer && tasks.length > 1 && (
          <section className="card taskrest">
            <div className="cardhead"><h3>Everything else on you</h3></div>
            <ul className="tasklist">
              {tasks.filter((t) => t !== first).map((t) => (
                <li key={t.key} className={t.done ? 'done' : ''}>
                  <button type="button" onClick={() => onGo(t.to[0], t.to[1])}>
                    <span className="what">
                      {t.done && <span className="tick" aria-hidden="true">✓</span>}
                      {t.title}
                    </span>
                    <span className={`pill ${t.done ? 'grey' : toneOf(t.urgency)}`}>
                      {taskState(t, now)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}

        {secondary.length > 0 && (
          <>
            <div className="alsohere"><span>Also here</span></div>
            <div className="homebody quiet">{secondary.map((s) => card(s.id))}</div>
          </>
        )}
      </Linkify>
    </div>
  );
}

/* =============================================================================== the sub-cards */

/** Draft day's lead: who is picking, when you are up, and what you have taken. */
function DraftLead(
  { due, state, made, total, since, seconds, onGo }: {
    due: ReturnType<typeof pickDue>;
    state: string | null;
    made: number;
    total: number;
    since: string | null;
    seconds: number | null;
    onGo: (s: string, p: string) => void;
  },
) {
  return (
    <section className={`card lead draftlead ${due.state}`}>
      <div className="cardhead">
        <h3>The draft</h3>
        {state === 'live' && <span className="pill live">LIVE</span>}
        <button type="button" className="linkbtn" onClick={() => onGo('draft', 'board')}>
          The board
        </button>
      </div>

      {due.state === 'off' ? (
        <p className="fact">
          {state === 'complete'
            ? 'The board is closed. Every pick is in.'
            : 'Not open yet. The board goes live when the commissioner starts it.'}
        </p>
      ) : (
        <>
          <div className="onclock">
            <b>{due.action || (due.onClock ? `${due.onClock.owner} is on the clock` : '')}</b>
            <span className="fact">
              {due.onClock ? `Pick ${due.onClock.code} · ${made} of ${total} made` : ''}
            </span>
            {/* One PickClock for every clock in the app (Pass 17): S here, on the light ground. */}
            <PickClock since={since} seconds={seconds} size="s" skin="light" className="clockfig" />
          </div>
        </>
      )}
    </section>
  );
}

/* ==================================================================================== helpers */

const byUrgency = (a: DeadlineRow, b: DeadlineRow) => {
  const rank: Record<Urgency, number> = { day: 0, week: 1, far: 2, passed: 3 };
  const u = rank[a.urgency] - rank[b.urgency];
  if (u !== 0) return u;
  const dir = a.urgency === 'passed' ? -1 : 1;
  return dir * (a.iso ?? '').localeCompare(b.iso ?? '');
};

const toneOf = (u: Urgency) =>
  (u === 'day' ? 'red' : u === 'week' ? 'amber' : u === 'passed' ? 'grey' : 'violet');

const taskState = (t: HomeTask, now: number) => {
  if (t.done) return 'done';
  if (!t.iso) return 'no date';
  if (t.urgency === 'passed') return 'closed';
  return liveLeft(t.iso, now)?.text ?? '';
};

/**
 * `5.06` back to an overall pick number.
 *
 * The strip states the code and `pickDue` wants the number; deriving it here rather than passing
 * both keeps one source for whose turn it is.
 */
function overallOfCode(code: string): number {
  const [r, p] = code.split('.').map(Number);
  if (!r || !p) return 0;
  return (r - 1) * TEAMS + p;
}
